import { useAppSelector } from "@app/hooks";
import { createSelector } from "@reduxjs/toolkit";
import { getVisibleProperties } from "@shared/utils/propertyFiltering";
import {
  selectCurrentSurvey,
  selectVisibleProperties,
} from "@survey/surveySelectors";
import { useMemo } from "react";

/**
 * Hook for components that need visible properties with optional filtering
 * Uses surveySelectors for consistent data access patterns
 *
 * @param propertyFilter - Optional property ID to filter for specific property
 * @returns Object containing survey and visible properties
 */
export const useVisibleProperties = (propertyFilter?: string) => {
  const survey = useAppSelector(selectCurrentSurvey);

  // If no filter is needed, use the existing selector
  const allVisibleProperties = useAppSelector(selectVisibleProperties);

  // Create filtered selector only when propertyFilter is provided
  const selectFilteredProperties = useMemo(
    () =>
      propertyFilter
        ? createSelector(
            [selectCurrentSurvey, selectVisibleProperties],
            (survey, properties) =>
              getVisibleProperties(properties, survey, propertyFilter),
          )
        : null,
    [propertyFilter],
  );

  const filteredProperties = useAppSelector(
    selectFilteredProperties || (() => allVisibleProperties),
  );

  return {
    survey,
    visibleProperties: propertyFilter
      ? filteredProperties
      : allVisibleProperties,
  };
};
